/**
 * SignLetterModal — read and sign a payment plan's acknowledgment letter (US 4396).
 *
 * The agent reviews the actual letter (the same PDF Bankers would email), types their full
 * name and confirms they agree. The signature is recorded with the date, their portal login
 * and IP; Bankers reviews it and activates the plan.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { OathModal, OathButton, OathAlert, OathSpinner } from '../../../shared/OathUI';
import { getPlanLetter, signPlanLetter } from '../../../../services/oathPaymentPlanService';

/**
 * Base64 PDF -> object URL. A `data:` URI in <object> only renders page 1 on iOS Safari,
 * so the letter is served as a Blob like PdfViewerModal does. Null when unavailable
 * (jsdom, or a malformed payload) — the caller falls back to the data URI.
 */
const toPdfObjectUrl = (base64) => {
  if (!base64 || typeof URL.createObjectURL !== 'function') return null;
  try {
    const bytes = atob(base64);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
    return URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
  } catch {
    return null;
  }
};

const SignLetterModal = ({ isOpen, agencyId, plan, onClose, onSigned }) => {
  const [pdf, setPdf] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !plan) return undefined;
    let cancelled = false;
    setPdf(null);
    setLoadError(null);
    setName('');
    setAgree(false);
    setError(null);
    setLoading(true);
    getPlanLetter(agencyId, plan.id).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result.success) setPdf(result.data);
      else setLoadError(result.error || 'Failed to load the letter.');
    });
    return () => { cancelled = true; };
  }, [isOpen, agencyId, plan]);

  const blobUrl = useMemo(() => toPdfObjectUrl(pdf), [pdf]);
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  if (!plan) return null;

  const pdfUrl = blobUrl || (pdf ? `data:application/pdf;base64,${pdf}` : null);

  const canSubmit = !!pdf && name.trim().length >= 2 && agree && !submitting;

  const handleSign = async () => {
    setSubmitting(true);
    setError(null);
    const result = await signPlanLetter(agencyId, plan.id, { signatureName: name.trim(), agree });
    setSubmitting(false);
    if (result.success) {
      toast.success(result.message || 'Letter signed. Bankers will review it and activate your plan.');
      onSigned();
      onClose();
    } else {
      setError(result.error || 'Failed to sign the letter.');
    }
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Acknowledgment letter — ${plan.name || `${plan.typeName || ''} payment plan`.trim()}`}
      size="2xl"
      persistent={submitting}
      footer={
        <div className="flex justify-end gap-3">
          <OathButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</OathButton>
          <OathButton onClick={handleSign} disabled={!canSubmit} loading={submitting}>
            Sign letter
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <OathSpinner size="lg" />
          </div>
        )}
        {loadError && <OathAlert variant="error" message={loadError} />}
        {pdfUrl && (
          <>
            <object
              data={pdfUrl}
              type="application/pdf"
              aria-label="Acknowledgment letter"
              className="w-full h-[55vh] rounded-xl border border-gray-200 dark:border-gray-700"
            >
              <a
                href={pdfUrl}
                download={`payment-plan-${plan.id}-letter.pdf`}
                className="text-primary underline"
              >
                Download the letter
              </a>
            </object>

            <div>
              <label htmlFor="sign-letter-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type your full name to sign
              </label>
              <input
                id="sign-letter-name"
                type="text"
                maxLength={100}
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>
                I have read this letter and agree to the payment plan terms. I understand that typing my name
                above is my electronic signature.
              </span>
            </label>
          </>
        )}
        {error && <OathAlert variant="error" message={error} />}
      </div>
    </OathModal>
  );
};

export default SignLetterModal;
